/* Studio Manager · Lumetier waitlist
   Lists every prospect who signed up via the public Lumetier landing page.
   Newest first, with a CSV export and a one-click delete for test entries. */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Download, Trash2, Mail, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const TYPE_LABELS = {
  wedding: "Wedding",
  family: "Family / portrait",
  newborn: "Newborn",
  branding: "Branding / commercial",
  event: "Event",
  other: "Other",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch (err) {
    console.warn("waitlist date parse failed", err);
    return iso;
  }
}

export default function AdminWaitlist() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/lumetier/waitlist");
      setEntries(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load waitlist");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(entry) {
    if (!window.confirm(`Remove ${entry.email} from the waitlist?`)) return;
    try {
      await api.delete(`/admin/lumetier/waitlist/${entry.id}`);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      toast.success("Removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't delete");
    }
  }

  function exportCSV() {
    if (!entries.length) return toast.error("Nothing to export yet.");
    const rows = [
      ["Name", "Email", "Photographer type", "Source", "Signed up"],
      ...entries.map((e) => [
        e.name || "",
        e.email || "",
        TYPE_LABELS[e.photographer_type] || e.photographer_type || "",
        e.source || "landing",
        e.created_at || "",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lumetier-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("Exported CSV.");
  }

  return (
    <div className="space-y-6" data-testid="admin-waitlist">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.32em] text-muted-foreground mb-2 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Lumetier · SaaS
          </div>
          <h1 className="text-3xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Charter waitlist
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every prospect who has signed up via the public Lumetier landing page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="waitlist-refresh-btn"
            className="inline-flex items-center gap-2 h-9 px-3 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={exportCSV}
            data-testid="waitlist-export-btn"
            className="inline-flex items-center gap-2 h-9 px-3 text-xs uppercase tracking-[0.18em] bg-foreground text-background hover:opacity-90"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="waitlist-stats">
        <Stat label="Total signups" value={entries.length} />
        <Stat label="Wedding" value={entries.filter((e) => e.photographer_type === "wedding").length} />
        <Stat label="Family / portrait" value={entries.filter((e) => e.photographer_type === "family").length} />
        <Stat label="Branding" value={entries.filter((e) => e.photographer_type === "branding").length} />
      </div>

      <div className="bg-card border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <tr>
                <th className="text-left py-3 px-4 font-normal">Name</th>
                <th className="text-left py-3 px-4 font-normal">Email</th>
                <th className="text-left py-3 px-4 font-normal hidden md:table-cell">Type</th>
                <th className="text-left py-3 px-4 font-normal hidden sm:table-cell">Signed up</th>
                <th className="py-3 px-4 font-normal w-10"></th>
              </tr>
            </thead>
            <tbody data-testid="waitlist-rows">
              {loading ? (
                <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">Loading…</td></tr>
              ) : entries.length === 0 ? (
                <tr><td colSpan={5} className="py-12 text-center text-muted-foreground" data-testid="waitlist-empty">
                  No signups yet. Share <a href="/lumetier" className="underline">/lumetier</a> to start collecting leads.
                </td></tr>
              ) : (
                entries.map((e) => (
                  <tr key={e.id} className="border-t border-border" data-testid={`waitlist-row-${e.id}`}>
                    <td className="py-3 px-4 font-medium">{e.name || "—"}</td>
                    <td className="py-3 px-4">
                      <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 hover:underline">
                        <Mail className="w-3 h-3 text-muted-foreground" /> {e.email}
                      </a>
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell text-muted-foreground">
                      {TYPE_LABELS[e.photographer_type] || e.photographer_type || "—"}
                    </td>
                    <td className="py-3 px-4 hidden sm:table-cell text-muted-foreground">{fmtDate(e.created_at)}</td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => handleDelete(e)}
                        title="Remove"
                        data-testid={`waitlist-delete-${e.id}`}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-card border border-border p-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
      <div className="text-3xl font-light mt-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{value}</div>
    </div>
  );
}
